// 되돌릴 수 없는 일을 한 번 더 묻는 두 모양과 오류 문장 꺼내기. 채널 머리·설정 판 여럿이 쓰므로
// 관리 판(`manage_panels`)과 떼어 둔다 — 같은 파일에 있으면 첫 화면이 관리 판까지 끌고 온다(청크는
// 모듈 단위다).
//
// - 글자 단추면 `Confirm`(단추 + 확인 창). 아이콘 단추·메뉴 항목처럼 여는 쪽이 따로 있으면 `ConfirmDialog`.
//   둘 다 창으로 묻는다 — 제자리 두 번 누르기는 없다.
// - 확정 단추의 이름은 결과를 말한다(`메시지 삭제`). `정말`·`확인` 은 무엇이 일어나는지 모른다.
// - 실패는 삼키지 않는다. 단추 곁(또는 창 안)에 까닭을 띄운다.

import { type ReactNode, useState } from 'react'
import { useT } from './i18n.ts'
import { Button } from './kit/button.tsx'
import { Dialog } from './kit/dialog.tsx'
import { trouble } from './trouble.ts'

/**
 * 누르면 확인 창이 뜨는 글자 단추. 지우기·거두기·로그아웃처럼 되돌리기 번거로운 일에 쓴다(슬랙처럼 창으로 묻는다 —
 * 제자리에서 단추가 [취소][확정] 둘로 갈라지면 옆 단추가 밀리고, 무엇이 사라지는지 말할 자리가 없었다).
 */
export function Confirm({
  label,
  confirm_label,
  act,
  icon,
  ask,
  description,
}: {
  label: string
  /** 확정 단추의 이름. 결과를 말한다(`채널 나가기`). */
  confirm_label: string
  act(): Promise<void>
  icon?: ReactNode
  /** 창의 묻는 말(`예약된 메시지를 삭제할까요?`). 없으면 확정 단추의 이름. */
  ask?: string
  /** 무엇이 사라지는지 한 줄. */
  description?: string
}) {
  const [open, set_open] = useState(false)
  return (
    <span className="Confirm">
      <Button size="sm" icon={icon} aria-haspopup="dialog" onClick={() => set_open(true)}>
        {label}
      </Button>
      <ConfirmDialog
        open={open}
        on_close={() => set_open(false)}
        title={ask ?? confirm_label}
        {...(description === undefined ? {} : { description })}
        confirm_label={confirm_label}
        act={act}
      />
    </span>
  )
}

export interface ConfirmDialogProps {
  open: boolean
  on_close(): void
  /** 묻는 말(`메시지를 삭제할까요?`). */
  title: string
  /** 무엇이 사라지는지 한 줄. */
  description?: string
  /** 확정 단추의 이름. 결과를 말한다(`메시지 삭제`). */
  confirm_label: string
  act(): Promise<void>
  /** 확정 단추의 색. 기본은 위험(빨강). 되돌릴 수 있는 바꿈(역할 바꾸기)은 `primary`. */
  confirm_variant?: 'danger' | 'primary'
}

/** 창으로 묻는 확인. 되면 닫히고, 안 되면 창 안에 까닭을 띄운 채 남는다. */
export function ConfirmDialog(props: ConfirmDialogProps) {
  const t = useT()
  const [busy, set_busy] = useState(false)
  const [trouble, set_trouble] = useState<string | null>(null)
  const close = () => {
    if (busy) return
    set_trouble(null)
    props.on_close()
  }
  return (
    <Dialog
      open={props.open}
      on_close={close}
      size="sm"
      title={props.title}
      description={props.description}
      className="ConfirmDialog"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={busy}>
            {t('취소')}
          </Button>
          <Button
            variant={props.confirm_variant ?? 'danger'}
            loading={busy}
            onClick={() => {
              set_busy(true)
              set_trouble(null)
              props
                .act()
                .then(
                  () => props.on_close(),
                  (e: unknown) => set_trouble(why(e)),
                )
                .finally(() => set_busy(false))
            }}
          >
            {props.confirm_label}
          </Button>
        </>
      }
    >
      {/* 첫 화면의 확인 창이라 `Notice`(아이콘 넷)를 끌어오지 않는다(+0.2 kB) — 같은 뜻을 이 줄이 칠한다. */}
      {trouble !== null && (
        <p role="alert" className="ConfirmDialog-trouble">
          {trouble}
        </p>
      )}
    </Dialog>
  )
}

/** 실패의 까닭 한 줄, 지금 화면 언어로. 가르는 규칙은 `trouble.ts` 에 있다. */
export function why(e: unknown): string {
  return trouble(e)
}
